import React, { ReactNode } from 'react';
import { Button, Modal, Stack, Form, Row, Col } from 'react-bootstrap';
import { useFormik } from 'formik';
import { RejectCandidateInitialValues, rejectCandidateSchema } from 'constants/schemaValidations';
import useTranslate from 'hooks/useTranslate';
import { RejectCandidateInterface } from 'constants/interfaces';
import toast from 'react-hot-toast';
import { useRejectCandidateMutation } from 'services';
import Loader from 'components/Common/Loader';
import useCommonStore from 'store/common';

const RejectCandidateModal: React.FC<any> = (props: any) => {
	const { translate } = useTranslate();
	const { mutateAsync: rejectCandidate, isPending } = useRejectCandidateMutation();
	const { hideCommonModal, modalConfig } = useCommonStore((state) => state);
	const formik: any = useFormik({
		initialValues: RejectCandidateInitialValues,
		validationSchema: rejectCandidateSchema(translate),
		enableReinitialize: true,
		onSubmit: (values: any) => {
			rejectCandidate({ id: props?.id, title: values?.reason, description: values?.comment.trim() })
				.then((res: any) => {
					if (res?.success) {
						toast.success(res?.message);
					}
				})
				.finally(() => {
					modalConfig?.refetch();
					hideCommonModal();
				});
		},
	});

	const renderError = <T extends keyof RejectCandidateInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	return (
		<Modal.Body>
			<Button
				className="close-btn rounded-circle border-0 fw-bold position-absolute"
				onClick={() => hideCommonModal()}
			>
				<i className="ri-close-line"></i>
			</Button>
			<Form className="edit-form" onSubmit={formik.handleSubmit}>
				<Stack className="gap-4">
					<h4 className="fw-bold mb-0">{translate('Modal.rejectCandidate')}</h4>
					<Row>
						<Col md="12">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Modal.reason')}</Form.Label>
								<Form.Control
									type="text"
									placeholder={translate('PlaceHolder.enterReason')}
									className="rounded-pill"
									name="reason"
									onChange={(e) => {
										let inputValue = e.target.value;

										// Remove leading spaces if present
										inputValue = inputValue.replace(/^\s+/, '');

										// Allow only one space between words
										inputValue = inputValue.replace(/\s{2,}/g, ' ');
										formik.setFieldValue('reason', inputValue);
									}}
									onBlur={formik.handleBlur}
									value={formik.values.reason}
									autoComplete="off"
								/>
								{renderError('reason')}
							</Form.Group>
						</Col>
						<Col md="12">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Modal.comment')}</Form.Label>
								<Form.Control
									type="textArea"
									placeholder={translate('Social.addComment')}
									className="rounded-pill"
									name="comment"
									onChange={(e) => {
										let inputValue = e.target.value;

										// Remove leading spaces if present
										inputValue = inputValue.replace(/^\s+/, '');

										// Allow only one space between words
										inputValue = inputValue.replace(/\s{2,}/g, ' ');
										formik.setFieldValue('comment', inputValue);
									}}
									onBlur={formik.handleBlur}
									value={formik.values.comment}
									autoComplete="off"
								/>
								{renderError('comment')}
							</Form.Group>
						</Col>
					</Row>
					<div className="edit-modal-actions d-flex gap-4">
						{isPending ? (
							<Loader />
						) : (
							<Button className="filled-btn rounded-pill" type="submit">
								{translate('Modal.reject')}
							</Button>
						)}
					</div>
				</Stack>
			</Form>
		</Modal.Body>
	);
};

export default RejectCandidateModal;
